
import InboxView from '../inbox/InboxView';
import VideosDisplay from './content/VideosDisplay';
import WeeklyCalendarView from '../calendar/WeeklyCalendarView';
import { SocialProfile } from './types';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import { SubscriptionLock } from '@/components/subscription/SubscriptionLock';
import { SubscriptionGuard } from '@/components/subscription/SubscriptionGuard';
import { Skeleton } from '@/components/ui/skeleton';

interface MainContentProps {
  activePage?: string;
  selectedProfiles: string[];
  singleSelectedProfile: string;
  isSingleSelectionMode: boolean;
  profiles?: SocialProfile[];
}

const MainContent = ({
  activePage,
  selectedProfiles,
  singleSelectedProfile,
  isSingleSelectionMode,
  profiles = [],
}: MainContentProps) => {
  const { isSubscriptionActive, isLoading: isSubscriptionLoading } = useSubscriptionStatus();

  // Si nous sommes sur la page d'accueil (boîte de réception)
  if (activePage === 'profiles') {
    // Pour la boîte de réception, pas de SubscriptionGuard pour éviter le flash
    return <div className="flex-1 overflow-auto h-full bg-pastel-blueLight dark:bg-[#121624]">
      <InboxView selectedProfiles={selectedProfiles} />
    </div>;
  }
  
  // Si nous sommes sur la page de contenu, afficher les vidéos
  if (activePage === 'content') {
    return (
      <div className="flex-1 overflow-auto h-full bg-pastel-blueLight dark:bg-[#121624]">
        <VideosDisplay 
          selectedProfiles={selectedProfiles}
          singleSelectedProfile={singleSelectedProfile}
          isSingleSelectionMode={isSingleSelectionMode}
          profiles={profiles}
        />
      </div>
    );
  }

  // Si nous sommes sur la page calendrier
  if (activePage === 'calendar') {
    return (
      <div className="flex-1 overflow-auto h-full bg-pastel-blueLight dark:bg-[#121624]">
        <WeeklyCalendarView selectedProfiles={selectedProfiles} />
      </div>
    );
  }
  
  // Afficher l'état de chargement pendant la vérification de l'abonnement pour les rapports
  if (activePage === 'reports' && isSubscriptionLoading) {
    return (
      <div className="flex-1 overflow-auto h-full bg-pastel-blueLight dark:bg-[#121624]">
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }
  
  // Si nous sommes sur la page rapports et que l'abonnement n'est pas actif
  if (activePage === 'reports' && isSubscriptionActive === false) {
    return (
      <div className="flex-1 overflow-auto h-full bg-pastel-blueLight dark:bg-[#121624]">
        <SubscriptionLock 
          title="Rapports verrouillés"
          description="Les rapports détaillés de vos profils ne sont disponibles qu'avec un abonnement actif."
          buttonText="Débloquer les rapports"
        />
      </div>
    );
  }
  
  // Pour les autres pages (comme "reports", etc.)
  return (
    <div className="flex-1 overflow-auto h-full bg-pastel-blueLight dark:bg-[#121624]">
      <div className="flex items-center justify-center h-full">
        <div className="text-center max-w-md px-6 py-10 rounded-xl bg-white/70 dark:bg-[#252A41]/50 backdrop-blur-sm shadow-sm border border-gray-100 dark:border-gray-800/30">
          <h2 className="text-lg font-medium text-custom-dark dark:text-gray-100 mb-3 font-sans">
            {activePage === 'reports' ? 'Rapports' : 
             activePage === 'analytics' ? 'Analytique' : 'Contenu'}
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 font-sans">
            {isSingleSelectionMode 
              ? singleSelectedProfile 
                ? `Affichage des statistiques pour le profil sélectionné` 
                : 'Veuillez sélectionner un profil pour voir les statistiques'
              : selectedProfiles.length > 0 
                ? `${selectedProfiles.length} profil(s) sélectionné(s)` 
                : 'Veuillez sélectionner au moins un profil'
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default MainContent;
